import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Shared auth state via the provider. This hook runs in AppContent, which is
// now wrapped by AuthProvider in App() — before that restructure AppContent
// rendered the provider itself, so this hook sat outside the context and had
// to keep its own useAuth() instance.
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';
import { onboardingStorage } from '../../services/Onboarding';
import { profileService } from '../../services/Profile/profileService';

export const useRoleBasedNavigation = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuthContext();

  useEffect(() => {
    // Don't navigate while loading or if not authenticated
    if (loading || !isAuthenticated || !user) {
      return;
    }

    // Get user role from metadata
    const userRole = user.user_metadata?.role || 'user';
    const currentPath = window.location.pathname;

    // If user is authenticated and onboarding is not complete, redirect to onboarding
    // (unless they're already on the onboarding page, splash screen, or a public route)
    const routesToIgnore = ['/', '/onboarding', '/login', '/registration', '/privacy-policy', '/terms-of-service', '/support'];
    const shouldIgnore = routesToIgnore.some(route =>
      currentPath === route || (route !== '/' && currentPath.startsWith(route + '/'))
    );

    if (shouldIgnore || currentPath.startsWith('/profile/setup')) {
      return;
    }

    // Fast path: this browser already knows onboarding is done, no need to
    // ask the backend.
    if (onboardingStorage.isOnboardingComplete()) {
      return;
    }

    // Local storage says incomplete, but that's per-browser — it could be
    // stale (onboarding was actually finished on another device/browser, or
    // this browser's storage was cleared). Confirm against the real,
    // per-account backend flag before redirecting back into onboarding.
    let cancelled = false;
    profileService.getOnboardingCompleted()
      .then((completed) => {
        if (cancelled) return;
        if (completed) {
          onboardingStorage.completeOnboarding();
          return;
        }
        logger.log('🧭 [NAVIGATION] Onboarding incomplete redirect');
        navigate('/onboarding', { replace: true });
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error('Failed to check onboarding completion, defaulting to onboarding:', error);
        navigate('/onboarding', { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated, loading, navigate]);

  // Helper function to get the appropriate dashboard URL based on user role
  const getDashboardUrl = (_userRole?: string) => {
    return '/social-feed';
  };

  // Helper function to check if user has admin role
  const isAdmin = () => {
    return user?.user_metadata?.role === 'admin';
  };

  // Helper function to check if user has premium role
  const isPremium = () => {
    return user?.user_metadata?.role === 'premium' || user?.user_metadata?.role === 'admin';
  };

  return {
    getDashboardUrl,
    isAdmin,
    isPremium,
    userRole: user?.user_metadata?.role || 'user'
  };
};
